/**
 * Name: Header
 */

import { throttle, debounce } from "throttle-debounce";
import Component from "@lunaweb/vega-reactor-js/src/patterns/Component";
import Navigation from "../Navigation/Navigation";
import Burger from "../Burger/Burger";

export default class Header extends Component {
  static settings = {};

  #burgerElement;
  #burger;
  #navigationElement;
  #navigation;
  #isSticky = false;
  #isPinned = false;
  #tempWindowWidth = 0;
  #tempWindowScroll = 0;
  #tempHeight;
  #needUpdate = true;

  #handleBurgerToggle;
  #handleNavigationUpdate;
  #handleTransitionEnd;
  #handleWindowDebouncedResize;
  #handleWindowThrottledScroll;

  constructor (element, options = {}) {
    super(element, {
      ...Header.settings,
      ...options
    });

    this.#burgerElement = document.getElementById('burger');
    this.#navigationElement = document.getElementById('navigation');

    this.#handleBurgerToggle = this._handleBurgerToggle.bind(this);
    this.#handleNavigationUpdate = this._handleNavigationUpdate.bind(this);
    this.#handleTransitionEnd = this._handleTransitionEnd.bind(this);
    this.#handleWindowDebouncedResize = debounce(20, this._handleWindowDebouncedResize.bind(this));
    this.#handleWindowThrottledScroll = throttle(20, this._handleWindowThrottledScroll.bind(this));
  }

  get burger () {
    return this.#burger;
  }

  get navigation () {
    return this.#navigation;
  }

  get isSticky () {
    return this.#isSticky;
  }

  get hasOpen () {
    return this.element.classList.contains('has-open');
  }

  set isSticky (state) {
    if (state) {
      this.element.classList.add('is-sticky');
    } else {
      this.element.classList.remove('is-sticky');
    }

    this.#isSticky = state;
  }

  get isPinned () {
    return this.#isPinned;
  }

  set isPinned (state) {
    if (state) {
      this.element.classList.add('is-pinned');
    } else {
      this.element.classList.remove('is-pinned');
    }

    this.#isPinned = state;
  }

  get windowWidth () {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  }

  _handleBurgerToggle () {
    if (this.#burger.isDisplayed && this.burger.expanded) {
      this.element.classList.add('has-open');
    } else {
      this.element.classList.remove('has-open');
    }
  }

  _handleNavigationUpdate () {
    if (!this.#burger.isDisplayed){
      if(this.navigation.currentItem !== null) {
        this.element.classList.add('has-open');
      } else {
        this.element.classList.remove('has-open');
      }
    }
  }

  _handleTransitionEnd (event) {
    this.element.classList.remove('has-transition');
    this.element.removeEventListener('transitionend', this.#handleTransitionEnd);
  }

  _handleWindowDebouncedResize () {
    if (this.#tempWindowWidth !== this.windowWidth) {
      this.update();

      this.#tempWindowWidth = this.windowWidth;
    }
  }

  _handleWindowThrottledScroll () {
    if(this.#tempWindowScroll !== window.scrollY) {
      let direction = this.#tempWindowScroll > window.scrollY ? "up" : "down";

      if(!this.isSticky && window.scrollY > this.element.offsetHeight + 16) {
        this.element.removeEventListener('transitionend', this.#handleTransitionEnd);
        this.element.classList.remove('has-transition');
        this.isSticky = true;
        this.isPinned = this.hasOpen;
      } else if(this.isSticky && window.scrollY <= this.element.offsetHeight + 16) {
        this.element.removeEventListener('transitionend', this.#handleTransitionEnd);
        this.element.classList.remove('has-transition');
        this.isSticky = false;
        this.isPinned = this.hasOpen;
      }

      if (direction == "up" && this.isSticky && !this.isPinned) {
        this.element.addEventListener('transitionend', this.#handleTransitionEnd);
        this.element.classList.add('has-transition');
        this.isPinned = true;
      } else if (direction == "down" && this.isSticky && this.isPinned) {
        this.element.addEventListener('transitionend', this.#handleTransitionEnd);
        this.element.classList.add('has-transition');
        this.isPinned = this.hasOpen;
      }

      this.#tempWindowScroll = window.scrollY;
    }
  }

  update () {
    if (this.element.offsetHeight !== this.#tempHeight || this.#needUpdate) {
      this.#tempHeight = this.element.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${ this.#tempHeight }px`);
    }

    if (this.burger && this.burger.isDisplayed && !this.burger.mounted) {
      this.burger.mount();
      this.navigation.mode = 'vertical';
    } else if (this.burger && !this.burger.isDisplayed && this.burger.mounted) {
      this.burger.unmount();
      this.navigation.mode = 'horizontal';
    }

    this.#needUpdate = false;
  }

  mount () {
    if (this.#navigationElement) {
      this.#navigation = new Navigation(this.#navigationElement);
      this.#navigation.on('update', this.#handleNavigationUpdate);
      this.#navigation.mount();
    }

    if (this.#burgerElement && this.#navigationElement) {
      this.#burger = new Burger(this.#burgerElement, {
        control: this.#burgerElement,
        target: this.#navigation.container,
      });

      if (this.#burger.isDisplayed) {
        this.#burger.mount();
        this.#navigation.mode = 'vertical';
      } else {
        this.#navigation.mode = 'horizontal';
      }

      this.#burger.on('toggle', this.#handleBurgerToggle);
    }

    window.addEventListener("resize", this.#handleWindowDebouncedResize);
    window.addEventListener('scroll', this.#handleWindowThrottledScroll);
    this.update();

    super.mount();
  }

  unmount () {
    super.unmount();

    window.removeEventListener("scroll", this.#handleWindowThrottledScroll);

    if (this.#burger && this.#navigation) {
      window.removeEventListener("resize", this.#handleWindowDebouncedResize);
    }

    if (this.#burger) {
      this.#burger.unmount();
    }

    if (this.#navigation) {
      this.#navigation.off('update', this.#handleNavigationUpdate);
      this.#navigation.unmount();
    }
  }
}
